export type AgeClock = "receipt" | "served" | "future";
export type AgeTier = "fresh" | "amber" | "red" | "unknown";

export interface RowAge {
  ageS: number | null;
  clock: AgeClock;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" ? value as Record<string, unknown> : null;
}

/** Newest receipt carried anywhere in the served body; malformed stamps are not knowledge. */
function newestReceiptMs(payload: unknown): number | null {
  const pending: unknown[] = [payload];
  const seen = new Set<object>();
  let newest: number | null = null;
  while (pending.length > 0) {
    const value = pending.pop();
    const body = record(value);
    if (body === null || seen.has(body)) continue;
    seen.add(body);
    if (typeof body.receipt_ts === "string") {
      const stamp = Date.parse(body.receipt_ts);
      if (Number.isFinite(stamp)) newest = newest === null ? stamp : Math.max(newest, stamp);
    }
    for (const child of Object.values(body)) {
      if (child !== null && typeof child === "object") pending.push(child);
    }
  }
  return newest;
}

export interface AgeAnchor {
  receiptMs: number | null;
  servedAge: unknown;
}

/** The payload walk, once per payload: the pill re-reads only rowAgeAt on its 1 s tick. */
export function ageAnchor(payload: unknown): AgeAnchor {
  return { receiptMs: newestReceiptMs(payload), servedAge: record(payload)?.age_s };
}

/**
 * One age clock. Two clocks on one body give the OLDER (R1 r2, DESK-n147 RULED 1 R8372): a door may serve an
 * age_s from vendor time beside a fresh receipt_ts, and the pill never reads fresher than its door's verdict.
 * The served age advances only from the fetch anchor that delivered it. A future receipt is named and clamped,
 * never rendered as a negative.
 */
export function rowAgeS(payload: unknown, fetchedAtMs: number | null, nowMs: number): RowAge {
  return rowAgeAt(ageAnchor(payload), fetchedAtMs, nowMs);
}

export function rowAgeAt(anchor: AgeAnchor, fetchedAtMs: number | null, nowMs: number): RowAge {
  const { receiptMs, servedAge } = anchor;
  const served = servedRowAge(servedAge, fetchedAtMs, nowMs);
  if (receiptMs !== null) {
    if (receiptMs > nowMs) return { ageS: 0, clock: "future" };
    const receiptAgeS = (nowMs - receiptMs) / 1_000;
    return served.ageS !== null && served.ageS > receiptAgeS ? served : { ageS: receiptAgeS, clock: "receipt" };
  }
  return served;
}

function servedRowAge(servedAge: unknown, fetchedAtMs: number | null, nowMs: number): RowAge {
  if (typeof servedAge !== "number" || !Number.isFinite(servedAge) || servedAge < 0
      || fetchedAtMs === null || !Number.isFinite(fetchedAtMs)) {
    return { ageS: null, clock: "served" };
  }
  return { ageS: servedAge + Math.max(0, nowMs - fetchedAtMs) / 1_000, clock: "served" };
}

/** AGE-TIER-RED-ANCHOR: the red branch is mutated by M-NO-RED. */
export function ageTier(ageS: number | null, budgetS: number | null): AgeTier {
  if (ageS === null || budgetS === null || !Number.isFinite(ageS) || !Number.isFinite(budgetS)
      || ageS < 0 || budgetS < 0) return "unknown";
  if (ageS <= budgetS) return "fresh";
  if (ageS <= 2 * budgetS) return "amber";
  return "red";
}

/** R5b: retention is legal only when every byte of the request key is unchanged. */
export function keptPayloadAfterFailure<T>(payload: T | null, payloadKey: string | null, requestKey: string): T | null {
  return payloadKey === requestKey ? payload : null;
}

/** A resolved non-2xx body is a refusal to render, never a replacement for the pill's last good row. */
export function lastGoodPayloadAfterResponse<T>(
  payload: T | null,
  payloadKey: string | null,
  requestKey: string,
  responseBody: T,
  status: number,
): T | null {
  return status >= 200 && status < 300
    ? responseBody
    : keptPayloadAfterFailure(payload, payloadKey, requestKey);
}

export function payloadBudgetS(payload: unknown): number | null {
  const value = record(payload)?.budget_s;
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

export function payloadRoot(payload: unknown): string {
  const value = record(payload)?.root;
  return typeof value === "string" && value.length > 0 ? value : "NONE";
}
